export type MemberManagementMode =
  | "CHILD_ONLY"
  | "SELF_ONLY"
  | "SELF_AND_CHILD";

export const MEMBER_MANAGEMENT_MODE_CHANGED_EVENT =
  "memberManagementModeChanged";

export const MEMBER_MANAGEMENT_MODE_OPTIONS = [
  {
    value: "SELF_ONLY",
    label: "会員本人",
    description: "本人だけが利用します。",
  },
  {
    value: "CHILD_ONLY",
    label: "家族の受講者",
    description: "会員が家族の受講者を管理します。",
  },
  {
    value: "SELF_AND_CHILD",
    label: "本人と家族の受講者",
    description: "本人と家族の受講者が利用します。",
  },
] as const satisfies readonly {
  value: MemberManagementMode;
  label: string;
  description: string;
}[];

export function memberManagementModeLabel(mode: MemberManagementMode) {
  return MEMBER_MANAGEMENT_MODE_OPTIONS.find((option) => option.value === mode)!
    .label;
}

export type MemberManagementModeSetting = {
  organizationId: string;
  facilityId: string | null;
  mode: MemberManagementMode | null;
  organizationMode: MemberManagementMode;
  effectiveMode: MemberManagementMode;
  version: number;
  organizationVersion: number;
  canManage: boolean;
};
export type OrganizationModeUpdate = {
  mode: MemberManagementMode;
  expectedVersion: number;
  reason?: string;
};
export type FacilityModeUpdate = Omit<OrganizationModeUpdate, "mode"> & {
  mode: MemberManagementMode | null;
  expectedOrganizationVersion: number;
};

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("設定の形式が不正です");
  return value as Record<string, unknown>;
}
function nonemptyString(value: unknown): string {
  if (typeof value !== "string" || !value.trim())
    throw new Error("設定の識別子が不正です");
  return value;
}
function version(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    throw new Error("設定のバージョンが不正です");
  return value;
}
function mode(value: unknown): MemberManagementMode {
  if (
    value !== "CHILD_ONLY" &&
    value !== "SELF_ONLY" &&
    value !== "SELF_AND_CHILD"
  )
    throw new Error("利用形態が不正です");
  return value;
}
function reason(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || value.trim().length > 500)
    throw new Error("変更理由は500文字以内で入力してください");
  return value.trim();
}

/** Unknown/old API responses are rejected; never replace them with a writable default. */
export function parseMemberManagementModeSetting(
  input: unknown
): MemberManagementModeSetting {
  const value = record(input);
  const parsed = {
    organizationId: nonemptyString(value.organizationId),
    facilityId:
      value.facilityId === null ? null : nonemptyString(value.facilityId),
    mode: value.mode === null ? null : mode(value.mode),
    organizationMode: mode(value.organizationMode),
    effectiveMode: mode(value.effectiveMode),
    version: version(value.version),
    organizationVersion: version(value.organizationVersion),
    canManage: value.canManage,
  };
  if (typeof parsed.canManage !== "boolean")
    throw new Error("設定の権限情報が不正です");
  if (
    parsed.facilityId === null &&
    (parsed.mode === null || parsed.mode !== parsed.organizationMode)
  )
    throw new Error("組織の利用形態が一致しません");
  if (parsed.effectiveMode !== (parsed.mode ?? parsed.organizationMode))
    throw new Error("利用形態の適用値が一致しません");
  return { ...parsed, canManage: parsed.canManage };
}

export function parseOrganizationModeUpdate(
  input: unknown
): OrganizationModeUpdate {
  const value = record(input);
  const parsedReason = reason(value.reason);
  return {
    mode: mode(value.mode),
    expectedVersion: version(value.expectedVersion),
    ...(parsedReason === undefined ? {} : { reason: parsedReason }),
  };
}
export function parseFacilityModeUpdate(input: unknown): FacilityModeUpdate {
  const value = record(input);
  const parsedReason = reason(value.reason);
  return {
    mode: value.mode === null ? null : mode(value.mode),
    expectedVersion: version(value.expectedVersion),
    expectedOrganizationVersion: version(value.expectedOrganizationVersion),
    ...(parsedReason === undefined ? {} : { reason: parsedReason }),
  };
}

export type MemberModeConflict = {
  facilityId: string;
  selfCount: number;
  childCount: number;
};
export function memberManagementModeFailure(error: unknown): {
  code: "MEMBER_MANAGEMENT_MODE_CONFLICT" | "MEMBER_MANAGEMENT_MODE_STALE";
  conflicts?: MemberModeConflict[];
} | null {
  try {
    const response = record(record(error).response);
    if (response.status !== 409) return null;
    const data = record(response.data);
    if (
      data.code !== "MEMBER_MANAGEMENT_MODE_CONFLICT" &&
      data.code !== "MEMBER_MANAGEMENT_MODE_STALE"
    )
      return null;
    if (data.conflicts === undefined) return { code: data.code };
    if (!Array.isArray(data.conflicts)) return null;
    return {
      code: data.code,
      conflicts: data.conflicts.map((item) => {
        const conflict = record(item);
        return {
          facilityId: nonemptyString(conflict.facilityId),
          selfCount: version(conflict.selfCount),
          childCount: version(conflict.childCount),
        };
      }),
    };
  } catch {
    return null;
  }
}
